import { useState } from "react";
import { motion } from "framer-motion";
import { Watch, PenLine, CheckCircle2, RefreshCw, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { sportIcon, fmtDistance, fmtDuration } from "../lib/workout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";

const RPE = [2, 4, 6, 8, 10];
const FELT = ["Easy", "Good", "Tough", "Too hard"];

export default function CompleteFlow({ workoutId, open, onOpenChange, garminConnected, onDone }) {
  const [step, setStep] = useState("choose"); // choose | candidates | feedback
  const [candidates, setCandidates] = useState([]);
  const [picked, setPicked] = useState(null); // external_id or null (manual)
  const [rpe, setRpe] = useState(6);
  const [felt, setFelt] = useState("Good");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setStep("choose"); setCandidates([]); setPicked(null); setRpe(6); setFelt("Good"); setNote(""); };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const fetchCandidates = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/workout/${workoutId}/garmin-candidates`);
      setCandidates(data.candidates || []);
      const sugg = (data.candidates || []).find((c) => c.suggested);
      setPicked(sugg ? sugg.external_id : (data.candidates?.[0]?.external_id ?? null));
      setStep("candidates");
      if (!data.candidates?.length) toast("No Garmin activities found for this day");
    } catch (e) {
      toast("Couldn't fetch from Garmin", { description: formatApiErrorDetail(e.response?.data?.detail) || "Try again" });
    } finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/workout/${workoutId}/complete`, {
        completed: true, rpe, felt, note: note || null,
        garmin_external_id: picked || null,
      });
      close(false);
      onDone(data);
      toast(data.analysis ? "Session analysed" : "Session logged — nice work");
    } catch (e) {
      toast("Couldn't save", { description: formatApiErrorDetail(e.response?.data?.detail) || "Try again" });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="complete-flow">
        <DialogHeader>
          <DialogTitle className="font-heading font-extrabold text-2xl tracking-tight">
            {step === "feedback" ? "How did it go?" : "Complete this session"}
          </DialogTitle>
        </DialogHeader>

        {step === "choose" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3 pt-1">
            <p className="text-sm text-muted-foreground">Pull it straight from your watch so I can analyse it properly — or just log it by hand.</p>
            {garminConnected && (
              <button data-testid="complete-sync-garmin" onClick={fetchCandidates} disabled={busy}
                className="w-full flex items-center gap-3 rounded-xl border border-primary/40 bg-primary/5 p-4 text-left hover:border-primary transition-colors">
                <div className="brand-gradient w-10 h-10 rounded-xl flex items-center justify-center shrink-0">
                  {busy ? <RefreshCw className="text-white animate-spin" size={18} /> : <Watch className="text-white" size={18} />}
                </div>
                <div>
                  <p className="font-heading font-bold text-foreground">Sync from Garmin</p>
                  <p className="text-xs text-muted-foreground">Fetch this day's activities & verify</p>
                </div>
              </button>
            )}
            <button data-testid="complete-manual" onClick={() => { setPicked(null); setStep("feedback"); }}
              className="w-full flex items-center gap-3 rounded-xl border border-border bg-card p-4 text-left hover:border-primary/50 transition-colors">
              <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center shrink-0"><PenLine className="text-muted-foreground" size={18} /></div>
              <div>
                <p className="font-heading font-bold text-foreground">Log manually</p>
                <p className="text-xs text-muted-foreground">No device data — just tell me how it went</p>
              </div>
            </button>
          </motion.div>
        )}

        {step === "candidates" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3 pt-1">
            <p className="text-sm text-muted-foreground">Which activity was this session?</p>
            <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="candidate-list">
              {candidates.map((c) => {
                const Icon = sportIcon(c.sport);
                const active = picked === c.external_id;
                return (
                  <button key={c.external_id} data-testid={`candidate-${c.external_id}`} onClick={() => setPicked(c.external_id)}
                    className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left transition-colors ${active ? "border-primary bg-primary/10" : "border-border bg-card hover:border-primary/50"}`}>
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${active ? "brand-gradient" : "bg-muted"}`}>
                      <Icon className={active ? "text-white" : "text-muted-foreground"} size={16} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-foreground truncate capitalize">{c.name || c.sport}</p>
                      <p className="text-xs text-muted-foreground">{fmtDistance(c.distance_m)} · {fmtDuration(c.duration_s)}{c.avg_hr ? ` · ${c.avg_hr} bpm` : ""}</p>
                    </div>
                    {c.suggested && <span className="text-[10px] font-bold uppercase tracking-wider text-brand-orange">match</span>}
                    {active && <CheckCircle2 size={18} className="text-primary shrink-0" />}
                  </button>
                );
              })}
              <button data-testid="candidate-none" onClick={() => setPicked(null)}
                className={`w-full rounded-xl border p-3 text-sm font-medium transition-colors ${picked === null ? "border-primary bg-primary/10 text-foreground" : "border-border bg-card text-muted-foreground hover:border-primary/50"}`}>
                None of these — log without device data
              </button>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep("choose")} className="rounded-xl"><ArrowLeft size={16} /></Button>
              <Button data-testid="candidates-next" onClick={() => setStep("feedback")} className="flex-1 h-11 rounded-xl font-semibold">Continue</Button>
            </div>
          </motion.div>
        )}

        {step === "feedback" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5 pt-1">
            <div>
              <p className="text-sm font-semibold mb-2">How hard was it? (effort 1–10)</p>
              <div className="flex gap-2" data-testid="complete-rpe">
                {RPE.map((r) => (
                  <button key={r} data-testid={`complete-rpe-${r}`} onClick={() => setRpe(r)}
                    className={`flex-1 py-2 rounded-full text-sm font-semibold border transition-colors ${rpe === r ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/50"}`}>{r}</button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold mb-2">How did it feel?</p>
              <div className="flex flex-wrap gap-2" data-testid="complete-felt">
                {FELT.map((f) => (
                  <button key={f} data-testid={`complete-felt-${f}`} onClick={() => setFelt(f)}
                    className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${felt === f ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/50"}`}>{f}</button>
                ))}
              </div>
            </div>
            <Textarea data-testid="complete-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything worth noting? (how you felt, terrain, niggles…)" rows={2} />
            <Button data-testid="complete-submit" onClick={submit} disabled={busy} className="w-full h-12 rounded-xl font-semibold active:scale-95 transition-transform">
              {busy ? "Caden is analysing…" : "Save & analyse"}
            </Button>
          </motion.div>
        )}
      </DialogContent>
    </Dialog>
  );
}
